import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "./AddressList.css";
import LocationPopup from "../LocationPopup/LocationPopup";
import { getUserAddress, setLocation } from "./../../redux/actions/action-creator";
import { AddressListSkeleton } from "../SkeletonLoader";
import { isGuestUser } from "../../utils/guestLogin";
import {
  LOCATION_STORAGE_KEY,
  getGuestLocationFromBrowser,
  isGuestDefaultLocation,
} from "../../utils/locationDefaults";

const getAddressItems = (response) => {
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.data?.addresses)) return response.data.addresses;
  return [];
};

const getAddressId = (item, index) =>
  String(item?.id || item?.address_id || item?.user_address_id || index);

const getLocationAddressId = (locationData) =>
  String(
    locationData?.selected_address_id ||
      locationData?.id ||
      locationData?.address_id ||
      locationData?.user_address_id ||
      ""
  );

const getCategoryLabel = (category, addressTitle) => {
  switch (String(category)) {
    case "1":
      return "Home";
    case "2":
      return "Work";
    case "3":
      return "Other";
    default:
      return addressTitle || "Unknown";
  }
};

const getCategoryIcon = (category) => {
  switch (String(category)) {
    case "2":
      return "ph ph-briefcase";
    case "3":
      return "ph ph-map-pin";
    default:
      return "ph ph-house";
  }
};

const getSavedAddressListLocation = () => {
  try {
    const savedLocation = localStorage.getItem(LOCATION_STORAGE_KEY);
    const parsedLocation = savedLocation ? JSON.parse(savedLocation) : null;

    if (isGuestDefaultLocation(parsedLocation)) {
      return null;
    }

    return parsedLocation?.selected_from_address_list ? parsedLocation : null;
  } catch (error) {
    return null;
  }
};

const isSameAddress = (item, savedLocation, index) => {
  if (!savedLocation) return false;

  const itemId = getAddressId(item, index);
  const savedId = getLocationAddressId(savedLocation);

  if (savedId && itemId === savedId) return true;

  return (
    String(item?.address || "") === String(savedLocation?.address || "") &&
    String(item?.latitude ?? item?.lat ?? "") ===
      String(savedLocation?.latitude ?? savedLocation?.lat ?? "") &&
    String(item?.longitude ?? item?.lng ?? "") ===
      String(savedLocation?.longitude ?? savedLocation?.lng ?? "")
  );
};

const getSelectedLocation = (item, index) => ({
  ...item,
  selected_address_id: getAddressId(item, index),
  latitude: item?.latitude ?? item?.lat,
  longitude: item?.longitude ?? item?.lng,
  address: item?.address,
  country_id: item?.country_id ?? item?.countryId,
  area: item?.area ?? item?.area_id,
  selected_from_address_list: true,
});

const AddressList = () => {

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user_location, userData = {} } = useSelector((state) => state.auth);
  const isGuest = isGuestUser(userData);
  const [loading, setLoading] = useState(false);
  const [addresses, setAddresses] = useState([]);
  const [open, setOpen] = useState(false);
  const [, setSelectedLocationText] = useState("Select Location");
  const [selectedId, setSelectedId] = useState("");
  const [openMenuId, setOpenMenuId] = useState(null);
  const [editData, setEditData] = useState(null);
  const [detectingLocation, setDetectingLocation] = useState(false);

  const handleMenuClick = (id) => {
    setOpenMenuId(openMenuId === id ? null : id);
  };

  useEffect(() => {
    if (!openMenuId) return undefined;

    const handleOutsideClick = (event) => {
      if (!event.target.closest(".address-menu")) {
        setOpenMenuId(null);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [openMenuId]);

  const handleonload = () => {
    setLoading(true);

    const data = new FormData();

    dispatch(getUserAddress(data))
      .then((response) => {
        const addressItems = getAddressItems(response);

        setAddresses(addressItems);

        const savedLocation =
          user_location?.selected_from_address_list
            ? user_location
            : getSavedAddressListLocation();
        const savedIndex = addressItems.findIndex((item, index) =>
          isSameAddress(item, savedLocation, index)
        );
        const apiSelectedIndex = addressItems.findIndex(
          (item) => String(item?.selected) === "1"
        );
        const selectedIndex =
          savedIndex >= 0 ? savedIndex : apiSelectedIndex;
        const fallbackIndex = selectedIndex >= 0 ? selectedIndex : 0;
        const selectedAddress = addressItems[fallbackIndex];

        setSelectedId(
          selectedAddress ? getAddressId(selectedAddress, fallbackIndex) : ""
        );
        if (selectedAddress) {
          const nextSelectedLocation = getSelectedLocation(selectedAddress, fallbackIndex);
          const currentSelectedId = getLocationAddressId(user_location || {});

          if (
            nextSelectedLocation.selected_address_id &&
            currentSelectedId !== nextSelectedLocation.selected_address_id
          ) {
            dispatch(setLocation(nextSelectedLocation));
          }
        }
        setLoading(false);
      })
      .catch(() => {
        setAddresses([]);
        setSelectedId("");
        setLoading(false);
      });
  };

  useEffect(() => {
    handleonload();
  }, []);

  const handleGuestLocationRetry = async () => {
    setDetectingLocation(true);

    try {
      const locationResult = await getGuestLocationFromBrowser();

      dispatch(setLocation(locationResult.location));

      if (!locationResult.permissionDenied) {
        navigate("/home", { replace: true });
        return;
      }

      toast.warn("Please allow location permission from your browser.", {
        position: "top-center",
      });
    } catch (error) {
      toast.warn("Unable to fetch your location.", {
        position: "top-center",
      });
    } finally {
      setDetectingLocation(false);
    }
  };

  const handleOpenAdd = () => {
    if (isGuest) {
      handleGuestLocationRetry();
      return;
    }

    setEditData(null);
    setOpen(true);
  };

  const handleUseCurrentLocation = () => {
    if (isGuest) {
      handleGuestLocationRetry();
      return;
    }

    handleOpenAdd();
  };

  const handleClosePopup = () => {
    setOpen(false);
    setEditData(null);
  };

  const handleEdit = (item) => {
    setOpenMenuId(null);
    setEditData(item);
    setOpen(true);
  };

  const handleDelete = () => {
    setOpenMenuId(null);
  };

  const handleSelectAddress = (item, index) => {
    const addressId = getAddressId(item, index);
    const selectedLocation = getSelectedLocation(item, index);

    setSelectedId(addressId);
    dispatch(setLocation(selectedLocation));
    navigate("/home");
  };

  return (
    <>
    <div className="container py-80 address-page-container">

      {/* Top Buttons */}
      <div className="address-top-buttons py-20">
        <button
          type="button"
          className="btn-custom location-btn"
          onClick={handleUseCurrentLocation}
          disabled={detectingLocation}
        >
          <i className="ph ph-crosshair" aria-hidden="true" />
          <span>{detectingLocation ? "Detecting..." : "Use current location"}</span>
        </button>

        <button
          type="button"
          className="btn-custom add-btn"
          onClick={handleOpenAdd}
          disabled={detectingLocation}
        >
          <i className="ph ph-plus-circle" aria-hidden="true" />
          <span>{detectingLocation ? "Detecting..." : "Add Address"}</span>
        </button>
      </div>

      <h2 className="address-section-title">Saved Addresses</h2>

      {/* Address List */}
      {loading ? (
        <AddressListSkeleton />
      ) : (
      <div className="address-list">

        {addresses.length > 0 ? (
          addresses.map((item, index) => {
            const addressId = getAddressId(item, index);
            const isSelected = selectedId === addressId;

            return (
              <div
                key={addressId}
                className={`address-card ${isSelected ? "selected" : ""}`}
              >
                <div
                  className="address-left"
                  onClick={() => handleSelectAddress(item, index)}
                >
                  <div className="address-name-row">
                    <div>
                      <span className={`radio ${isSelected ? "active" : ""}`} />
                    </div>

                    <span className="name">{item.user_name || "Address"}</span>
                  </div>

                  <div className="address-text">{item.address}</div>
                </div>

                <div className="address-right">
                  {item.outOfService && (
                    <span className="out-badge">Out of service area</span>
                  )}

                  <span className="type-badge">
                    <i className={getCategoryIcon(item.category)} aria-hidden="true" />
                    <span>{getCategoryLabel(item.category, item.address_title)}</span>
                  </span>

                  <div className="address-menu">
                    <button
                      type="button"
                      className="menu"
                      onClick={() => handleMenuClick(addressId)}
                      aria-label="Address options"
                      aria-expanded={openMenuId === addressId}
                    >
                      ⋮
                    </button>

                    {openMenuId === addressId && (
                      <div className="dropdown">
                        <button type="button" onClick={() => handleEdit(item)}>
                          <i className="ph ph-pencil-simple" aria-hidden="true" />
                          <span>Edit</span>
                        </button>
                        <button type="button" onClick={handleDelete}>
                          <i className="ph ph-trash" aria-hidden="true" />
                          <span>Delete</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="address-empty">No saved address found.</div>
        )}

      </div>
      )}
    </div>
    <LocationPopup
      open={open}
      onClose={handleClosePopup}
      onSelect={(addr) => setSelectedLocationText(addr)}
      editData={editData}
      onSaved={handleonload}
    />
    </>
  );
};

export default AddressList;
